import { Typography, Badge } from '@lucid/lego';
import type { Prospect } from '../../../../api/services/lupp/prospect/dto.prospect';

export const getStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'blue';
    case 'contacting':
      return 'orange';
    case 'engaged':
      return 'green';
    case 'converted':
      return 'fuchsia';
    case 'quote generated':
    case 'quotes generated':
      return 'indigo';
    case 'quote accepted':
    case 'quotes accepted':
      return 'purple';
    case 'ordered':
      return 'teal';
    case 'delivered':
      return 'darktaupe';
    case 'closed':
      return 'gray';
    default:
      return 'gray';
  }
};

export const getPriorityBadgeColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'informative';
    case 'low':
      return 'warning';
    default:
      return 'neutral';
  }
};

export const getInterestedModelDisplayName = (model: string) => {
  switch (model?.toLowerCase()) {
    case 'lucid-air':
      return 'Lucid Air';
    case 'lucid-gravity':
      return 'Lucid Gravity';
    default:
      return model;
  }
};

interface FieldType {
  label: string;
  key: string;
  value: string | number | boolean | null | string[];
}

interface SectionType {
  label: string;
  fields: FieldType[];
}

export interface ProspectDetailType {
  prospectIdentifier: string;
  prospectName: string;
  situation?: SectionType;
  prospectStatusProgression?: SectionType;
  purchaseProfile?: SectionType;
  keyFields?: SectionType;
  information?: SectionType;
}

interface CreateColumnsParams {
  theme: 'light' | 'dark';
  onEditClick: (record: Prospect) => void;
  labels: Record<string, string>;
  locale?: string;
}

export const createColumns = ({
  theme,
  onEditClick,
  labels,
  locale = 'en-US',
}: CreateColumnsParams) => [
  {
    key: 'statusName',
    title: labels?.status || 'Status',
    width: '100px',
    align: 'left' as const,
    sortable: true,
    render: (_: unknown, record: Prospect) => {
      const status = record.statusName?.toLowerCase() || 'open';
      // Map status to localized label
      const statusLabelMap: Record<string, string> = {
        open: labels?.['const.PROSPECT_STATUS_STEPS.open'] ?? 'Open',
        contacting: labels?.['const.PROSPECT_STATUS_STEPS.contacting'] ?? 'Contacting',
        engaged: labels?.['const.PROSPECT_STATUS_STEPS.engaged'] ?? 'Engaged',
        'quote generated':
          labels?.['const.PROSPECT_STATUS_STEPS.quoteGenerated'] ?? 'Quote generated',
        'quote accepted': labels?.['const.PROSPECT_STATUS_STEPS.quoteAccepted'] ?? 'Quote accepted',
        ordered: labels?.['const.PROSPECT_STATUS_STEPS.ordered'] ?? 'Ordered',
        delivered: labels?.['const.PROSPECT_STATUS_STEPS.delivered'] ?? 'Delivered',
        closed: labels?.['const.PROSPECT_STATUS_STEPS.closed'] ?? 'Closed',
      };
      const statusLabel =
        statusLabelMap[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
      return (
        <Badge
          label={statusLabel}
          color={getStatusBadgeColor(status)}
          type="secondary"
          theme={theme === 'dark' ? 'light' : 'dark'}
          minWidth="60px"
        />
      );
    },
  },
  {
    key: 'prospectFirstName',
    title: labels.full_name || 'Full Name',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography
        variant="body"
        size="small"
        theme={theme}
        color="link"
        onClick={() => onEditClick(record)}
      >
        {`${record.prospectFirstName || ''} ${record.prospectLastName || ''}`.trim() || '-'}
      </Typography>
    ),
  },
  {
    key: 'prospectEmail',
    title: labels.email || 'Email',
    width: '200px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.prospectEmail || '-'}
      </Typography>
    ),
  },
  {
    key: 'prospectPhone',
    title: labels.phone || 'Phone',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.prospectPhone || '-'}
      </Typography>
    ),
  },
  {
    key: 'zipCode',
    title: labels.postal_code || 'Postal Code',
    width: '120px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.zipCode || '-'}
      </Typography>
    ),
  },
  {
    key: 'city',
    title: labels?.city || 'City',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.city || record.region || '-'}
      </Typography>
    ),
  },
  {
    key: 'prospectSourceName',
    title: labels.source_of_prospect || 'Source of Prospect',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.prospectCaptureForm || record.prospectSourceName || record.sourceName || '-'}
      </Typography>
    ),
  },
  {
    key: 'nextEngagementDate',
    title: labels.next_engagement_date || 'Next Engagement Date',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.nextEngagementDate
          ? new Date(record.nextEngagementDate).toLocaleDateString(locale, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '-'}
      </Typography>
    ),
  },
  {
    key: 'assignedOwner',
    title: labels?.['util.assignedOwner'] ?? 'Assigned Owner',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.assignedOwner || '-'}
      </Typography>
    ),
  },
];

export const createProspectDetail = (
  record: Prospect,
  labels: Record<string, string> = {}
): ProspectDetailType => ({
  prospectIdentifier: String(record.prospectIdentifier),
  prospectName: `${record.prospectFirstName || '-'} ${record.prospectLastName || '-'}`,
  situation: {
    label: labels.situation,
    fields: [
      {
        label: labels.situational_notes,
        key: 'noteText',
        value: record.noteText || labels.default_placeholder,
      },
      {
        label: labels.objections,
        key: 'objections',
        value: record.objections || labels.default_placeholder,
      },
      {
        label: labels.important_features,
        key: 'importantFeatures',
        value: record.importantFeatures || labels.default_placeholder,
      },
    ],
  },
  purchaseProfile: {
    label: labels.purchase_profile,
    fields: [
      {
        label: labels.interested_model,
        key: 'interestedModel',
        value: record.interestedModel || (labels.default_placeholder as string),
      },
      {
        label: labels.interested_trim,
        key: 'interestedTrim',
        value: record.interestedTrim || (labels.default_placeholder as string),
      },
      {
        label: labels.purchase_type,
        key: 'purchaseType',
        value: record.purchaseType || (labels.default_placeholder as string),
      },
      {
        label: labels.purchase_timing,
        key: 'purchaseTiming',
        value: record.purchaseTiming || (labels.default_placeholder as string),
      },
      {
        label: labels.finance_type,
        key: 'financeType',
        value: record.financeType || (labels.default_placeholder as string),
      },
    ],
  },
  information: {
    label: labels.information,
    fields: [
      {
        label: labels.company_name,
        key: 'companyName',
        value: record.prospectCompanyName || labels.default_placeholder,
      },
      {
        label: labels.preferred_contact_method,
        key: 'preferredContactMethod',
        value: record.preferredContactMethod || labels.default_placeholder,
      },
      {
        label: labels.preferred_language,
        key: 'preferredLanguageIso',
        value: record.preferredLanguageIso || labels.default_placeholder,
      },
    ],
  },
});
